import TonalButton from '@/components/TonalButton';
import { useContext, useState } from 'react';
import { StyleOutlined as TagsIcon } from '@mui/icons-material';
import { type Tag } from '@/lib/types';
import PortfolioItemContext from '../../../../../PortfolioItemContext';
import useApi from '@/lib/api/useApi';
import styled from '@emotion/styled';
import useRandomizedId from '@/lib/utils/useRandomizedId';
import useGlobalStaticApiResponse from '@/lib/api/useGlobalStaticApiResponse';
import TagSelector from './TagSelector';
import { FormattedMessage } from 'react-intl';

const AddButton = styled(TonalButton<'button'>)({
  marginBottom: 8,
  height: 34,
});

const AddTags = () => {
  const api = useApi();
  const {
    portfolioItemApiEndpoint,
    setPortfolioItem,
    setPortfolioItemSummary,
  } = useContext(PortfolioItemContext);

  const [loading, allTags] =
    useGlobalStaticApiResponse<Record<number, Tag>>('tags');

  const popoverId = useRandomizedId('add-tags-popover');
  const [popoverAnchorElement, setPopoverAnchorElement] =
    useState<HTMLButtonElement | null>(null);

  const [saving, setSaving] = useState(false);

  const saveChanges = async (selectedTags: number[]): Promise<void> => {
    setSaving(true);

    const fullTags = selectedTags
      .map(id => allTags![id])
      .filter(tag => typeof tag !== 'undefined');

    await api.post(`${portfolioItemApiEndpoint}/tags`, {
      tags: fullTags.map(tag => tag.id),
    });

    setPortfolioItem(portfolioItem => {
      portfolioItem.tags = portfolioItem.tags.concat(fullTags);
    });

    setPortfolioItemSummary(portfolioItemSummary => {
      portfolioItemSummary.tags = portfolioItemSummary.tags.concat(fullTags);
    });

    setSaving(false);
  };

  return (
    <>
      <AddButton
        aria-describedby={popoverAnchorElement !== null ? popoverId : undefined}
        onClick={e => setPopoverAnchorElement(e.currentTarget)}
        startIcon={<TagsIcon />}
        loadingPosition="start"
        loading={loading || saving}
      >
        <FormattedMessage id="portfolio.challenge.tags_field.add_tags.button" />
      </AddButton>
      <TagSelector
        id={popoverId}
        anchorElement={popoverAnchorElement}
        allTags={allTags ?? {}}
        onSave={saveChanges}
        onClose={() => setPopoverAnchorElement(null)}
      />
    </>
  );
};

export default AddTags;
